import React, { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AnimatePresence, motion as Motion } from 'framer-motion';
import { AuthProvider, useAuth } from './context/AuthContext';
import { JobProvider } from './context/JobContext';
import LandingPage from './components/LandingPage';
import AuthPage from './components/AuthPage';
import ErrorBoundary from './components/ErrorBoundary';
import Analytics from './components/Analytics';
import { Toaster } from 'react-hot-toast';
import { connectTarget } from './utils/connectTarget';

// Only the entry pages (landing, auth) ship in the main bundle. Everything
// else - especially the editor with react-virtuoso - loads on demand, so
// first paint isn't stuck behind one monolithic chunk.
const TranscriptEditor = lazy(() => import('./components/TranscriptEditor'));
const ProjectDashboard = lazy(() => import('./components/ProjectDashboard'));
const ForgotPasswordPage = lazy(() => import('./components/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./components/ResetPasswordPage'));
const VerifyEmailPage = lazy(() => import('./components/VerifyEmailPage'));
const DashboardLayout = lazy(() => import('./components/layout/DashboardLayout'));
const AboutPage = lazy(() => import('./components/AboutPage'));
const PrivacyPage = lazy(() => import('./components/PrivacyPage'));
const TermsPage = lazy(() => import('./components/TermsPage'));
const CheckoutSuccess = lazy(() => import('./components/CheckoutSuccess'));
const BillingPage = lazy(() => import('./components/BillingPage'));
const PremierePluginPage = lazy(() => import('./components/PremierePluginPage'));
const NotFoundPage = lazy(() => import('./components/NotFoundPage'));
const AdminPage = lazy(() => import('./components/AdminPage'));
const DeviceConnect = lazy(() => import('./components/DeviceConnect'));

const Spinner = () => (
  <div className="flex h-screen w-screen items-center justify-center bg-[#FDFBF7] dark:bg-stone-950">
    <div className="h-10 w-10 rounded-full border-4 border-stone-900 dark:border-stone-100 border-t-transparent dark:border-t-transparent animate-spin" />
  </div>
);

/** Redirect to /app if already logged in */
function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <Spinner />;
  if (user) return <Navigate to={connectTarget(location.search) || '/app'} replace />;
  return children;
}

/** Render the 404 page unless the logged-in user is an admin, so the route's
    existence isn't advertised to regular users or logged-out visitors. */
function AdminRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <Spinner />;
  if (!user || user.role !== 'admin') return <NotFoundPage />;
  return children;
}

/** Redirect to /login if not logged in */
function PrivateRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <Spinner />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

const PageWrapper = ({ children }) => (
  <Motion.div
    initial={{ opacity: 0, y: 15 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -15 }}
    transition={{ duration: 0.3, ease: 'easeInOut' }}
    className="h-full w-full"
  >
    {children}
  </Motion.div>
);

/**
 * Mounts JobProvider keyed on the logged-in user's id: when the session changes
 * (logout, or a different account signs in) React remounts the provider, wiping
 * the previous user's job/burn state so stale burn polling can't leak across
 * sessions. Within one session the key is stable, so job state survives navigation.
 */
const SessionScopedJobProvider = ({ children }) => {
  const { user } = useAuth();
  return <JobProvider key={user?.id || 'anonymous'}>{children}</JobProvider>;
};

const AnimatedRoutes = () => {
  const location = useLocation();
  return (
    // Suspense sits OUTSIDE AnimatePresence: a lazy chunk load shows the
    // spinner without breaking exit animations on non-suspending navigations.
    <Suspense fallback={<Spinner />}>
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        <Route path="/" element={<PublicRoute><PageWrapper><LandingPage /></PageWrapper></PublicRoute>} />
        <Route path="/login" element={<PublicRoute><PageWrapper><AuthPage mode="login" /></PageWrapper></PublicRoute>} />
        <Route path="/register" element={<PublicRoute><PageWrapper><AuthPage mode="register" /></PageWrapper></PublicRoute>} />
        <Route path="/connect" element={<PageWrapper><DeviceConnect /></PageWrapper>} />
        <Route path="/forgot-password" element={<PublicRoute><PageWrapper><ForgotPasswordPage /></PageWrapper></PublicRoute>} />
        {/* Not wrapped in PublicRoute: clicking a reset link while logged in
            (e.g. from another device) must still reach the form. The page logs
            the user in on success and redirects to /app. */}
        <Route path="/reset-password/:token" element={<PageWrapper><ResetPasswordPage /></PageWrapper>} />
        {/* Not wrapped in PublicRoute: verifying logs the user in, and we still
            want to render the success screen before redirecting to /app. */}
        <Route path="/verify-email/:token" element={<PageWrapper><VerifyEmailPage /></PageWrapper>} />
        <Route path="/about" element={<PublicRoute><PageWrapper><AboutPage /></PageWrapper></PublicRoute>} />
        <Route path="/privacy" element={<PublicRoute><PageWrapper><PrivacyPage /></PageWrapper></PublicRoute>} />
        <Route path="/terms" element={<PublicRoute><PageWrapper><TermsPage /></PageWrapper></PublicRoute>} />
        <Route path="/success" element={<PrivateRoute><PageWrapper><CheckoutSuccess /></PageWrapper></PrivateRoute>} />
        
        <Route path="/app" element={<PrivateRoute><PageWrapper><DashboardLayout /></PageWrapper></PrivateRoute>}>
           <Route index element={<Navigate to="dashboard" replace />} />
           <Route path="dashboard" element={<ProjectDashboard />} />
           <Route path="upload" element={<ProjectDashboard />} />
           <Route path="billing" element={<BillingPage />} />
           <Route path="premiere" element={<PremierePluginPage />} />
           <Route path="editor/:jobId" element={<TranscriptEditor />} />
        </Route>

        <Route path="/admin" element={<AdminRoute><PageWrapper><AdminPage /></PageWrapper></AdminRoute>} />

        <Route path="*" element={<PageWrapper><NotFoundPage /></PageWrapper>} />
      </Routes>
    </AnimatePresence>
    </Suspense>
  );
};

import { HelmetProvider } from 'react-helmet-async';

function App() {
  return (
    <HelmetProvider>
      {/* No app-wide <Helmet titleTemplate>: under React 19 Helmet renders each
          instance's <title> on its own, so the template never applied and the
          default rendered as a second <title>. Pages write their full title, and
          the prerendered <title> in the HTML covers routes that set none. */}
      <ErrorBoundary>
        <BrowserRouter>
          <Analytics />
          <AuthProvider>
             {/* JobProvider must live ABOVE the keyed <Routes> — AnimatedRoutes keys the
                 route tree on location.pathname for page transitions, so anything inside
                 it (including DashboardLayout) remounts on every navigation. Mounting the
                 provider here keeps burn tracking / auto-download / upload state alive
                 while the user moves between dashboard, editor, and billing. */}
             <SessionScopedJobProvider>
               <AnimatedRoutes />
             </SessionScopedJobProvider>
          </AuthProvider>
        </BrowserRouter>
      </ErrorBoundary>
    </HelmetProvider>
  );
}

export default App;
